/**
 * Pure helpers for the cross-channel conversation thread.
 *
 * Dependency-light on purpose: the one rule below is needed by BOTH bubble
 * renderers (the shared `ConversationView` and MemberDetail's drifted inline
 * copy) and by their tests. Living here rather than beside the modal keeps the
 * rule importable without dragging in React, supabase and the dispatch client.
 */

/** The fields of a thread message this module needs. */
export interface ThreadMessageLike {
  readonly channel: string;
  readonly vendor_message_id: string | null;
}

/**
 * Whether a thread bubble can be opened as a full email.
 *
 * Gated on the CHANNEL, not just on an id being present: every channel stores
 * something in `vendor_message_id` — a wamid on WhatsApp, an IG message id on
 * Instagram — and none of those mean anything to `read_gmail`. Without the
 * channel check the affordance would appear on WhatsApp bubbles and fail on
 * every click.
 *
 * Shared so the two bubble renderers cannot disagree about which bubbles are
 * clickable.
 */
export function isOpenableEmail(
  msg: ThreadMessageLike,
): msg is ThreadMessageLike & { vendor_message_id: string } {
  return (
    msg.channel === "email" &&
    msg.vendor_message_id !== null &&
    msg.vendor_message_id.length > 0
  );
}

/**
 * Which vendor holds an email's full copy, decided by id NAMESPACE: Gmail
 * ids are bare lowercase hex, Resend ids are dashed UUIDs — the same
 * disjointness the 2026-07-21 threading backfill relies on. The email viewer
 * routes on this (dashed → `read_resend_email`, bare → `read_gmail`), so
 * every historical row works without a stored vendor column or a migration.
 */
export function isResendMessageId(id: string): boolean {
  return id.includes("-");
}
